// ─────────────────────────────────────────────────────────────────────────────
// AMMO.JS ANCHOR SYSTEM — binds soft-body cloth nodes to skeleton rigid bodies
//
// Implements btSoftBody::appendAnchor(nodeIndex, rigidBody, disableCollision, influence)
// This is what makes the cloth follow the skeleton while still simulating physics.
//
// Usage flow:
//   1. AnchorSystem.buildRigidBodies(pose)   — create btRigidBody per bone
//   2. AnchorSystem.bindAnchors(softBody, schema.anchors)  — attach cloth nodes
//   3. Each frame: AnchorSystem.updateRigidBodies(pose)   — move rigid bodies
// ─────────────────────────────────────────────────────────────────────────────

import type * as THREE from "three";
import type { GarmentAnchor } from "./garment-schema";
import type { RetargetPose } from "./retarget";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AmmoLib = any;

// ── Map bone name → world position from RetargetPose ─────────────────────────
function boneWorldPos(boneName: string, pose: RetargetPose): THREE.Vector3 | null {
  switch (boneName) {
    case "Hips":          return pose.hipCenter;
    case "Spine":         return pose.spineCenter;
    case "LeftShoulder":  return pose.leftShoulder;
    case "RightShoulder": return pose.rightShoulder;
    default:              return null;
  }
}

export class AnchorSystem {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private rigidBodies: Map<string, any> = new Map(); // boneName → btRigidBody
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private motionStates: Map<string, any> = new Map(); // boneName → btDefaultMotionState
  private anchorsApplied = false;

  constructor(
    private Ammo: AmmoLib,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private world: any  // btSoftRigidDynamicsWorld
  ) {}

  // ── Build a kinematic rigid body at each anchor bone position ────────────
  // Call once after the first valid pose is available.
  buildRigidBodies(pose: RetargetPose, boneNames: string[]) {
    const Ammo = this.Ammo;

    for (const boneName of boneNames) {
      if (this.rigidBodies.has(boneName)) continue;

      const worldPos = boneWorldPos(boneName, pose);
      if (!worldPos) continue;

      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.getOrigin().setValue(worldPos.x, worldPos.y, worldPos.z);

      const motionState = new Ammo.btDefaultMotionState(transform);
      this.motionStates.set(boneName, motionState);

      // Zero mass = kinematic / static — pose drives it directly each frame
      const shape  = new Ammo.btSphereShape(0.08);
      const inertia = new Ammo.btVector3(0, 0, 0);
      const rbInfo  = new Ammo.btRigidBodyConstructionInfo(0, motionState, shape, inertia);
      const rb      = new Ammo.btRigidBody(rbInfo);
      rb.setCollisionFlags(rb.getCollisionFlags() | 2); // CF_KINEMATIC_OBJECT
      rb.setActivationState(4); // DISABLE_DEACTIVATION

      this.world.addRigidBody(rb);
      this.rigidBodies.set(boneName, rb);
    }

    console.log(`[AnchorSystem] Built ${this.rigidBodies.size} rigid bodies`);
  }

  // ── Bind soft-body cloth nodes to rigid bodies via appendAnchor ──────────
  // Call ONCE after buildRigidBodies and after the soft body is created.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bindAnchors(softBody: any, anchors: GarmentAnchor[]) {
    if (this.anchorsApplied) return;

    let bound = 0;
    for (const anchor of anchors) {
      const rb = this.rigidBodies.get(anchor.bone);
      if (!rb) {
        console.warn(`[AnchorSystem] No rigid body for bone "${anchor.bone}"`);
        continue;
      }

      softBody.appendAnchor(
        anchor.nodeIndex,
        rb,
        anchor.disableCollision,
        anchor.influence
      );
      bound++;
    }

    this.anchorsApplied = true;
    console.log(`[AnchorSystem] Bound ${bound}/${anchors.length} anchors`);
  }

  // ── Per-frame: move kinematic rigid bodies to follow skeleton pose ────────
  updateRigidBodies(pose: RetargetPose) {
    const Ammo = this.Ammo;
    const transform = new Ammo.btTransform();

    for (const [boneName, rb] of this.rigidBodies) {
      const worldPos = boneWorldPos(boneName, pose);
      if (!worldPos) continue;

      transform.setIdentity();
      transform.getOrigin().setValue(worldPos.x, worldPos.y, worldPos.z);

      const ms = this.motionStates.get(boneName);
      if (ms) ms.setWorldTransform(transform);

      rb.setWorldTransform(transform);
      rb.activate();

      Ammo.destroy(transform);
    }
  }

  // ── Tear down all rigid bodies ────────────────────────────────────────────
  dispose() {
    const Ammo = this.Ammo;
    for (const rb of this.rigidBodies.values()) {
      this.world.removeRigidBody(rb);
      Ammo.destroy(rb);
    }
    for (const ms of this.motionStates.values()) {
      Ammo.destroy(ms);
    }
    this.rigidBodies.clear();
    this.motionStates.clear();
    this.anchorsApplied = false;
  }
}
